import React, { FC, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Icons } from '@components/Icon';
import useTranslations from '@hooks/useTranslation';
import RadioTemplate from '@ui/Radio/Radio';
import { formatLecturerName } from '@utils/person.utils';
import { capitalizeFirstLetter, removeAccents } from '@utils/string.utils';
import {
    ConferenceTypes,
    CourseTypes,
    FilterTypesInResponse,
    FilterTypesInUrl,
    IFilterItem,
    IPackageFilterItem,
    MonthLabels,
} from '../../interfaces/Filter';
import { ILecturerItem } from '../../interfaces/Lecturer';
import CheckboxTemplate from '../../ui/Checkbox/Checkbox';
import {
    Group,
    Icon,
    InLine,
    Input,
    Item,
    Line,
    RemoveResult,
    Search,
    SearchResult,
    SearchResults,
    SelectedResult,
    SelectedResults,
    Title,
    Wrapper,
} from './Filter.elements';

export interface FilterProps {
    name: string;
    isLecturer?: boolean;
    data?: IFilterItem[] | ILecturerItem[] | IPackageFilterItem;
    type?: FilterTypesInResponse;
    courseTypeCounts?: { [key in CourseTypes]: number };
    conferenceTypeCounts?: { [key in ConferenceTypes]: number };
    withBorderBottom?: boolean;
}

const FilterTemplate: FC<FilterProps> = ({
    name,
    isLecturer = false,
    data,
    type,
    courseTypeCounts,
    conferenceTypeCounts,
    withBorderBottom,
}) => {
    const { t } = useTranslations();

    const router = useRouter();

    const [selectedCities, setSelectedCities] = useState(router.query[FilterTypesInUrl.CITY]);
    const [selectedMonths, setSelectedMonths] = useState(router.query[FilterTypesInUrl.MONTH]);
    const [selectedPackages, setSelectedPackages] = useState(router.query[FilterTypesInUrl.PACKAGES]);
    const [selectedTrainingTypes, setSelectedTrainingTypes] = useState(router.query[FilterTypesInUrl.TRAINING_TYPE]);
    const [selectedConferenceTypes, setSelectedConferenceTypes] = useState(
        router.query[FilterTypesInUrl.CONFERENCE_TYPE] || ConferenceTypes.UPCOMING
    );
    const [selectedParentCategories, setSelectedParentCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState(router.query[FilterTypesInUrl.CATEGORY]);
    const [selectedLecturers, setSelectedLecturers] = useState(router.query[FilterTypesInUrl.LECTURER]);
    const [searchLecturers, setSearchLecturers] = useState(null);

    const searchInputRef = useRef<HTMLInputElement>();

    useEffect(() => {
        setSelectedCities(router.query[FilterTypesInUrl.CITY]);
        setSelectedMonths(router.query[FilterTypesInUrl.MONTH]);
        setSelectedPackages(router.query[FilterTypesInUrl.PACKAGES]);
        setSelectedTrainingTypes(router.query[FilterTypesInUrl.TRAINING_TYPE] || CourseTypes.UPCOMING);
        setSelectedConferenceTypes(router.query[FilterTypesInUrl.CONFERENCE_TYPE] || ConferenceTypes.UPCOMING);
        setSelectedCategories(router.query[FilterTypesInUrl.CATEGORY]);
        setSearchLecturers(router.query[FilterTypesInUrl.LECTURER]);
    }, [router.query]);

    const updateUrlParameters = async (
        newValue: string,
        type: FilterTypesInUrl,
        previousValues: string | string[],
        updateValues: (values: string | string[]) => void,
        parentCategory?: string,
        childrenCategories?: string[]
    ) => {
        const query = router.query;

        delete query.strana;

        const arrValues = previousValues ? (previousValues as string)?.split(',') : [];

        // Add or remove category if it has not a parent
        if (!parentCategory && arrValues?.filter((category) => childrenCategories?.includes(category))?.length === 0) {
            arrValues.includes(newValue)
                ? (query[type] = arrValues.filter((city) => city !== newValue).join(','))
                : (query[type] = [...arrValues, newValue].join(','));
        }

        // Add or remove category if it has a parent
        if (parentCategory) {
            arrValues.includes(newValue)
                ? (query[type] = arrValues.filter((city) => city !== newValue).join(','))
                : (query[type] = [...arrValues, newValue].join(','));
        }

        // Remove all children if parent was unselected
        if (query[type] && type === FilterTypesInUrl.CATEGORY && childrenCategories) {
            query[type] = (query[type] as String)
                ?.split(',')
                .filter((category) => !childrenCategories.includes(category))
                .join(',');
        }

        // Remove parent category if child was selected
        if (query[type] && type === FilterTypesInUrl.CATEGORY && parentCategory) {
            query[type] = (query[type] as String)
                ?.split(',')
                .filter((category) => category !== parentCategory)
                .join(',');
        }

        updateValues(query[type]);

        if (!query[type]) delete query[type];

        return router.push({
            pathname: router.pathname,
            query,
        });
    };

    const search = () => {
        const searchInput = searchInputRef?.current?.value;

        if (searchInput?.length >= 3) {
            setSearchLecturers(
                (data as ILecturerItem[])?.filter((lecturer: ILecturerItem) =>
                    removeAccents(formatLecturerName(lecturer).toLowerCase()).includes(
                        removeAccents(searchInput?.toLowerCase())
                    )
                )
            );
        } else {
            setSearchLecturers(null);
        }
    };

    useEffect(() => {
        if (type === FilterTypesInResponse.CATEGORY) {
            const selectedParentCategoriesArr = [] as string[];

            data.map((filter) => {
                const allChildKeys = (filter as IFilterItem)?.children?.map((child) => child._key);
                const selectedChildKeys = (selectedCategories as string)?.split(',');

                if (!!selectedChildKeys?.find((selectedKey) => allChildKeys.includes(selectedKey))) {
                    selectedParentCategoriesArr.push(filter._key);
                }
            });

            setSelectedParentCategories(selectedParentCategoriesArr);
        }
    }, [selectedCategories]);

    const removeSelectedParentCategory = (removedCategoryKey: string) => {
        const query = router.query;

        setSelectedParentCategories(selectedParentCategories.filter((key) => key !== removedCategoryKey));

        const selectedChildrenKeys = (selectedCategories as string)?.split(',') || [];

        const parentCategory = (data as IFilterItem[]).find((filter) => filter._key === removedCategoryKey);
        const parentChildrenKeys = (parentCategory as IFilterItem).children.map((child) => child._key);

        const updatedChildrenKeys = selectedChildrenKeys.filter(
            (selectedCategory) => !parentChildrenKeys.includes(selectedCategory)
        );

        if (updatedChildrenKeys?.length === 0) {
            delete query[FilterTypesInUrl.CATEGORY];
        } else {
            query[FilterTypesInUrl.CATEGORY] = updatedChildrenKeys.join(',');
        }

        const removedChildCategories =
            (data as IFilterItem[])
                ?.find((category) => category._key === removedCategoryKey)
                ?.children?.map((category) => category._key) || [];

        setSelectedCategories(
            ((selectedCategories as string)?.split(',') || [])
                .filter((selectedCategory) => !removedChildCategories.includes(selectedCategory))
                .join(',')
        );
        return router.push({
            pathname: router.pathname,
            query,
        });
    };

    return (
        <Wrapper withBorderBottom={withBorderBottom}>
            <Title>{name}</Title>
            {isLecturer ? (
                <>
                    <SelectedResults>
                        {selectedLecturers?.length > 0 &&
                            (selectedLecturers as string)?.split(',')?.map((selectedLecturerKey) => (
                                <SelectedResult key={selectedLecturerKey}>
                                    <RemoveResult
                                        onClick={() => {
                                            updateUrlParameters(
                                                selectedLecturerKey,
                                                FilterTypesInUrl.LECTURER,
                                                selectedLecturers,
                                                setSelectedLecturers
                                            );
                                            searchInputRef.current.value = '';
                                        }}
                                    >
                                        X
                                    </RemoveResult>
                                    {formatLecturerName(
                                        (data as ILecturerItem[])?.find(
                                            (lecturer: ILecturerItem) => lecturer._key === selectedLecturerKey
                                        )
                                    )}
                                </SelectedResult>
                            ))}
                    </SelectedResults>
                    <Search>
                        <Icon name={Icons.Search} />
                        <Input onKeyUp={search} ref={searchInputRef} placeholder="Jméno a příjmení" />
                        {searchInputRef?.current?.value?.length > 0 && searchLecturers?.length > 0 && (
                            <SearchResults>
                                {searchLecturers.map((lecturer: ILecturerItem, key: number) => {
                                    if (
                                        !(selectedLecturers as string)
                                            ?.split(',')
                                            ?.find((selectedLecturerKey) => lecturer._key === selectedLecturerKey)
                                    ) {
                                        return (
                                            <SearchResult
                                                onClick={() => {
                                                    updateUrlParameters(
                                                        lecturer._key,
                                                        FilterTypesInUrl.LECTURER,
                                                        selectedLecturers,
                                                        setSelectedLecturers
                                                    );
                                                    searchInputRef.current.value = '';
                                                }}
                                                key={key}
                                            >
                                                {formatLecturerName(lecturer)}
                                            </SearchResult>
                                        );
                                    }
                                })}
                            </SearchResults>
                        )}
                    </Search>
                </>
            ) : (
                <>
                    {type === FilterTypesInResponse.CITY &&
                        data?.map((filter, key: number) => {
                            return (
                                <Line key={`city-${filter._key}-${key}`}>
                                    <CheckboxTemplate
                                        name={`city-${filter?._key}`}
                                        onChange={() =>
                                            updateUrlParameters(
                                                filter._key,
                                                FilterTypesInUrl.CITY,
                                                selectedCities,
                                                setSelectedCities
                                            )
                                        }
                                        checked={selectedCities?.includes(filter._key)}
                                        label={`${capitalizeFirstLetter(filter.name)} (${(filter as IFilterItem).count
                                            })`}
                                    />
                                </Line>
                            );
                        })}
                    {type === FilterTypesInResponse.PACKAGES &&
                        Object?.keys(data).map((filter, key: number) => {
                            return (
                                <Line key={`packages-${filter}-${key}`}>
                                    <CheckboxTemplate
                                        name={`packages-${filter}`}
                                        onChange={() =>
                                            updateUrlParameters(
                                                filter,
                                                FilterTypesInUrl.PACKAGES,
                                                selectedPackages,
                                                setSelectedPackages
                                            )
                                        }
                                        checked={selectedPackages?.includes(filter)}
                                        label={`${t(`filter.package.${filter}`)} (${data?.[filter]?.count})`}
                                    />
                                </Line>
                            );
                        })}
                    {type === FilterTypesInResponse.MONTH &&
                        // @ts-ignore
                        [...new Set(data.map((item) => item.year))]
                            ?.sort((a, b) => a - b)
                            ?.map((year, key) => {
                                return (
                                    <div key={key}>
                                        <Group>{year}</Group>
                                        {data
                                            ?.filter((filter: IFilterItem) => filter?.year === year)
                                            ?.map((filter: IFilterItem, key: number) => (
                                                <Line key={`month-${(filter as IFilterItem).month}-${key}`}>
                                                    <CheckboxTemplate
                                                        name={`month-${(filter as IFilterItem)?.month}-${(filter as IFilterItem)?.year
                                                            }`}
                                                        onChange={() =>
                                                            updateUrlParameters(
                                                                `${(filter as IFilterItem).month}-${(filter as IFilterItem).year
                                                                }`,
                                                                FilterTypesInUrl.MONTH,
                                                                selectedMonths,
                                                                setSelectedMonths
                                                            )
                                                        }
                                                        checked={
                                                            !!(selectedMonths as string)
                                                                ?.split(',')
                                                                ?.find(
                                                                    (selectedMonthKey) =>
                                                                        selectedMonthKey ===
                                                                        `${(filter as IFilterItem).month}-${(filter as IFilterItem).year
                                                                        }`
                                                                )
                                                        }
                                                        label={`${(MonthLabels as { [key in string]: string })[
                                                            String((filter as IFilterItem).month)
                                                        ]
                                                            } (${(filter as IFilterItem).count})`}
                                                    />
                                                </Line>
                                            ))}
                                    </div>
                                );
                            })}
                    {type === FilterTypesInResponse.CATEGORY &&
                        data
                            ?.filter((filter: IFilterItem) => filter?.count > 0)
                            ?.map((filter, parentKey: number) => {
                                return (
                                    <Item key={`cat-${filter._key}-${parentKey}`}>
                                        <Line key={`category-${filter._key}-${parentKey}`}>
                                            <CheckboxTemplate
                                                name={`category-${filter?._key}`}
                                                onChange={() =>
                                                    updateUrlParameters(
                                                        filter._key,
                                                        FilterTypesInUrl.CATEGORY,
                                                        selectedCategories,
                                                        setSelectedCategories,
                                                        null,
                                                        (filter as IFilterItem)?.children?.map((child) => child?._key)
                                                    )
                                                }
                                                checked={
                                                    !!(selectedCategories as string)
                                                        ?.split(',')
                                                        ?.find(
                                                            (selectedCategoryKey) => selectedCategoryKey === filter._key
                                                        ) ||
                                                    !!selectedParentCategories?.find(
                                                        (selectedCategoryKey) => selectedCategoryKey === filter._key
                                                    )
                                                }
                                            >
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: `${filter.name.replace(' a ', '</br>a ')} (${filter.count
                                                            })`,
                                                    }}
                                                />
                                            </CheckboxTemplate>
                                        </Line>
                                        {(selectedCategories?.includes(filter._key) ||
                                            !!selectedParentCategories?.find(
                                                (selectedCategoryKey) => selectedCategoryKey === filter._key
                                            )) &&
                                            (filter as IFilterItem)?.children?.map((child, childKey: number) => {
                                                return (
                                                    <InLine key={`children-category-${child._key}-${childKey}`}>
                                                        <CheckboxTemplate
                                                            name={child._key}
                                                            onChange={() =>
                                                                updateUrlParameters(
                                                                    child._key,
                                                                    FilterTypesInUrl.CATEGORY,
                                                                    selectedCategories,
                                                                    setSelectedCategories,
                                                                    filter?._key
                                                                )
                                                            }
                                                            checked={
                                                                !!(selectedCategories as string)
                                                                    ?.split(',')
                                                                    ?.find(
                                                                        (selectedCategoryKey) =>
                                                                            selectedCategoryKey === child._key
                                                                    )
                                                            }
                                                            label={`${child.name} (${child.count})`}
                                                        />
                                                    </InLine>
                                                );
                                            })}
                                    </Item>
                                );
                            })}
                    {type === FilterTypesInResponse.TRAINING_TYPE && (
                        <>

                            <Line>
                                <RadioTemplate
                                    name={CourseTypes.UPCOMING}
                                    onChange={() =>
                                        updateUrlParameters(
                                            CourseTypes.UPCOMING,
                                            FilterTypesInUrl.TRAINING_TYPE,
                                            '',
                                            setSelectedTrainingTypes
                                        )
                                    }
                                    checked={selectedTrainingTypes?.includes(CourseTypes.UPCOMING) ? selectedTrainingTypes?.includes(CourseTypes.UPCOMING) : false}
                                    label={`${t(`filters.courseType.upcoming`)} (${courseTypeCounts[CourseTypes.UPCOMING] || 0
                                        })`}
                                />
                            </Line>

                            {selectedTrainingTypes?.includes(CourseTypes.UPCOMING) &&
                                [CourseTypes.ONLINE, CourseTypes.ATTENDANCE].map((courseType, key) => (
                                    <InLine key={key}>
                                        <CheckboxTemplate
                                            name={courseType}
                                            onChange={() =>
                                                updateUrlParameters(
                                                    courseType,
                                                    FilterTypesInUrl.TRAINING_TYPE,
                                                    selectedTrainingTypes,
                                                    setSelectedTrainingTypes
                                                )
                                            }
                                            checked={selectedTrainingTypes?.includes(courseType)}
                                            label={`${t(`filters.courseType.${courseType}`)} (${courseTypeCounts[courseType] || 0
                                                })`}
                                        />
                                    </InLine>
                                ))}

                            <Line>
                                <RadioTemplate
                                    name={CourseTypes.ARCHIVED}
                                    onChange={() =>
                                        updateUrlParameters(
                                            CourseTypes.ARCHIVED,
                                            FilterTypesInUrl.TRAINING_TYPE,
                                            '',
                                            setSelectedTrainingTypes
                                        )
                                    }
                                    checked={selectedTrainingTypes?.includes(CourseTypes.ARCHIVED) ? selectedTrainingTypes?.includes(CourseTypes.ARCHIVED) : false}
                                    label={`${t(`filters.courseType.archived`)} (${courseTypeCounts[CourseTypes.ARCHIVED] || 0
                                        })`}
                                />
                            </Line>

                        </>
                    )}
                    {type === FilterTypesInResponse.CONFERENCE_TYPE &&
                        [ConferenceTypes.UPCOMING, ConferenceTypes.ARCHIVED].map((conferenceType, key) => (
                            <Line key={key}>
                                <RadioTemplate
                                    name={String(conferenceType)}
                                    onChange={() => {
                                        updateUrlParameters(
                                            String(conferenceType),
                                            FilterTypesInUrl.CONFERENCE_TYPE,
                                            null,
                                            setSelectedConferenceTypes
                                        );
                                    }}
                                    checked={selectedConferenceTypes?.includes(String(conferenceType))}
                                    label={`${t(`filters.conferenceType.${conferenceType}`)} (${conferenceTypeCounts[conferenceType] || 0
                                        })`}
                                />
                            </Line>
                        ))}
                </>
            )}
        </Wrapper>
    );
};

export default FilterTemplate;
